import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Modules } from '~/data/modules';
import {
  saveUserPreferencesData,
  useGetUserPreferencesData,
} from '~/data/userSettings';
import { useHasChanged } from '~/hooks/useHasChanged';
import { useVisible } from '~/hooks/useVisible';
import { useAppModules } from '~/scenes/AppModules/useAppModules';

export const useWorkspaceHomepageCard = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { isModuleActive } = useAppModules();
  const { isWorkspaceEnabled } = useMemo(() => {
    return { isWorkspaceEnabled: isModuleActive(Modules.WORKSPACE) };
  }, [isModuleActive]);

  const { data, isLoading, failedToLoad, mutate } = useGetUserPreferencesData(
    !isWorkspaceEnabled,
  );

  const { useWorkspaceAsHomepage } = useMemo(() => {
    return {
      useWorkspaceAsHomepage: data?.useWorkspaceAsHomepage ?? false,
    };
  }, [data]);

  const { isVisible, show, hide } = useVisible();

  const form = useForm({
    defaultValues: { useWorkspaceAsHomepage: useWorkspaceAsHomepage ?? false },
  });
  const { reset, handleSubmit } = form;

  const hasDataChanged = useHasChanged(useWorkspaceAsHomepage);
  useEffect(() => {
    if (hasDataChanged) {
      reset({ useWorkspaceAsHomepage: useWorkspaceAsHomepage ?? false });
    }
  }, [reset, hasDataChanged, useWorkspaceAsHomepage]);

  const onSubmit = useMemo(() => {
    return handleSubmit((data) => {
      return saveUserPreferencesData(data)
        .then(() => {
          mutate();
          hide();
          enqueueSnackbar(t('message_success_approval'), {
            variant: 'success',
          });
        })
        .catch(() => {
          enqueueSnackbar(t('laErrorSavingData'), {
            variant: 'error',
          });
        });
    });
  }, [t, enqueueSnackbar, handleSubmit, hide, mutate]);

  const onCancelEdit = useCallback(() => {
    reset(data);
    hide();
  }, [reset, data, hide]);

  return {
    form,
    editMode: isVisible,
    onEdit: show,
    onCancelEdit,
    onSubmit,
    isLoading,
    failedToLoad,
    isWorkspaceEnabled,
  };
};
